// Value-slot keyword/snippet emission (T7, D13) — encodes the plan's field
// table verbatim. Legality is role- and engine-gated here; the emit module
// calls this once per slot.

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import type { ValueSlotRole } from '@/features/sql-grammar/model';
import { KEYWORD_TIER } from './tiers';

/** Symbolic comparison operators that admit quantified subqueries
 *  (`ANY`/`ALL`/`SOME`) — never LIKE/ILIKE (P2-11). */
const QUANTIFIABLE_OPERATORS: ReadonlySet<string> = new Set([
  '=', '<>', '!=', '<', '>', '<=', '>=',
]);

const QUANTIFIER_ENGINES: ReadonlySet<DatabaseEngineId> = new Set([
  'pgsql', 'mysql', 'mssql', 'oracle',
]);

/** TRUE/FALSE literals (boolean-ish LHS only). Oracle included post-C3: the
 *  booleanish gate is itself the version gate — pre-23ai Oracle cannot
 *  declare a BOOLEAN column, so `lhsBooleanish` can only be true on servers
 *  that accept the literals (verified live on 23ai/26ai: DATA_TYPE
 *  introspects as BOOLEAN and `WHERE flag = TRUE` executes). */
const TRUE_FALSE_ENGINES: ReadonlySet<DatabaseEngineId> = new Set([
  'pgsql', 'mysql', 'sqlite', 'oracle',
]);

/** Engines whose VALUES list accepts the DEFAULT keyword (D13). Exported for
 *  the resolved-target path (T18a) — resolvers build `legalSources.keywords`
 *  from the same table. Oracle included post-C3 after live verification:
 *  `VALUES (DEFAULT)` applies the declared default, or NULL when none. */
export const DEFAULT_ENGINES: ReadonlySet<DatabaseEngineId> = new Set([
  'pgsql', 'mysql', 'sqlite', 'mssql', 'oracle',
]);

/** `(SELECT …)` — legal at every value position; shared by the slot-role
 *  path (`emitValueSlotKeywords`) and the resolved-target path
 *  (`emitResolvedValueCandidates`) so the candidate shape cannot drift. */
export function pushScalarSubquerySnippet(out: CompletionCandidate[]): void {
  out.push({
    label: '(SELECT …)',
    insertText: '(SELECT $1)',
    isSnippet: true,
    filterText: 'SELECT',
    kind: 'snippet',
    tier: KEYWORD_TIER,
    detail: 'scalar subquery',
  });
}

export type ValueSlotKeywordArgs = {
  readonly engine: DatabaseEngineId;
  readonly role: ValueSlotRole;
  /** Normalized upper operator ('=', 'LIKE', …); undefined for roles without
   *  an operator (set-rhs, values — later slices). */
  readonly operator?: string;
  readonly lhsBooleanish: boolean;
};

export function emitValueSlotKeywords(
  out: CompletionCandidate[],
  args: ValueSlotKeywordArgs,
): void {
  const { engine, role, operator, lhsBooleanish } = args;

  // `(SELECT …)` — all engines, all roles.
  pushScalarSubquerySnippet(out);

  // ANY / ALL / SOME — symbolic comparison operators ONLY (never LIKE).
  if (
    operator !== undefined &&
    QUANTIFIABLE_OPERATORS.has(operator) &&
    QUANTIFIER_ENGINES.has(engine)
  ) {
    for (const kw of ['ANY', 'ALL', 'SOME'] as const) {
      out.push({
        label: kw,
        insertText: `${kw} (SELECT $1)`,
        isSnippet: true,
        kind: 'snippet',
        tier: KEYWORD_TIER,
        detail: 'quantified subquery',
      });
    }
  }

  // TRUE / FALSE — boolean-ish LHS on pg/mysql/sqlite/oracle.
  if (lhsBooleanish && TRUE_FALSE_ENGINES.has(engine)) {
    for (const kw of ['TRUE', 'FALSE'] as const) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: KEYWORD_TIER,
        detail: 'boolean literal',
      });
    }
  }

  // NULL — set-rhs / values roles ONLY (D13: suppressed at comparison RHS as
  // a UX choice steering to IS NULL, not claimed illegal). T16: this used to
  // be `role !== 'comparison-rhs'`, an implicit "everything but comparison"
  // allowlist that would have wrongly emitted NULL at the new between-low/
  // between-high roles too (`BETWEEN NULL AND …` is nonsensical — NOT the
  // same UX steer as comparison RHS's IS NULL suggestion). Flipped to an
  // explicit allowlist so a future role defaults to NULL-absent, not
  // NULL-present.
  if (role === 'set-rhs' || (role as string) === 'values') {
    out.push({
      label: 'NULL',
      insertText: 'NULL',
      isSnippet: false,
      kind: 'keyword',
      tier: KEYWORD_TIER,
      detail: 'SQL keyword',
    });
  }

  // DEFAULT — values role only.
  if ((role as string) === 'values' && DEFAULT_ENGINES.has(engine)) {
    out.push({
      label: 'DEFAULT',
      insertText: 'DEFAULT',
      isSnippet: false,
      kind: 'keyword',
      tier: KEYWORD_TIER,
      detail: 'SQL keyword',
    });
  }
}
